import React, { useEffect, useMemo, useState } from 'react';
import { listPlayers, registerPlayer } from '../api/americanas';
import './AmericanaRegisterForm.css';
import 'font-awesome/css/font-awesome.min.css';
import { capitalizeWords, formatFullName } from '../utils/nameUtils';

export default function AmericanaRegisterForm({
                                                  americanaId,
                                                  currentUserId,
                                                  isAdmin,
                                                  canRegisterOthers = false,
                                                  onRegistered,
                                                  capacity,
                                                  confirmedCount,
                                              }) {
    const [players, setPlayers] = useState([]);
    const [playerId, setPlayerId] = useState('');
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const p = await listPlayers();
                setPlayers(p);
                if (!canRegisterOthers && currentUserId) {
                    setPlayerId(currentUserId);
                }
            } catch (e) {
                setError(e?.response?.data?.message || 'Error cargando listado de jugadoras');
            }
        })();
    }, [americanaId, canRegisterOthers, currentUserId]);

    const options = useMemo(() => {
        return players
            .map((player) => {
                const displayName =
                    formatFullName(player?.name, player?.lastName) ||
                    formatFullName(player?.firstName, player?.lastName) ||
                    capitalizeWords(player?.name || player?.firstName || '') ||
                    (player?.id ? `Jugadora #${player.id}` : '');
                const normalizedDisplayName = displayName.trim() || 'Jugadora sin identificar';

                return { ...player, displayName: normalizedDisplayName };
            })
            .sort((a, b) => a.displayName.localeCompare(b.displayName));
    }, [players]);

    const { nextStatus, remainingSlots, showCapacityInfo } = useMemo(() => {
        const numericCapacity = Number(capacity);
        const hasCapacity = Number.isFinite(numericCapacity) && numericCapacity > 0;
        if (!hasCapacity) {
            return { nextStatus: 'CONFIRMED', remainingSlots: null, showCapacityInfo: false };
        }
        const remaining = Math.max(numericCapacity - (confirmedCount ?? 0), 0);
        return {
            nextStatus: remaining > 0 ? 'CONFIRMED' : 'WAITLIST',
            remainingSlots: remaining,
            showCapacityInfo: true,
        };
    }, [capacity, confirmedCount]);

    const submit = async () => {
        setError(null);
        const effectivePlayerId = canRegisterOthers ? playerId : currentUserId;

        if (!effectivePlayerId) {
            setError('No se ha podido identificar a la jugadora. Intenta recargar.');
            return;
        }

        setSaving(true);
        try {
            await registerPlayer(americanaId, Number(effectivePlayerId), nextStatus);
            onRegistered && onRegistered();

            if (canRegisterOthers) {
                setPlayerId('');
            }
        } catch (e) {
            setError(e?.response?.data?.message || 'Error al realizar la inscripción.');
        } finally {
            setSaving(false);
        }
    };

    const getButtonText = () => {
        if (saving) return 'Procesando...';
        if (nextStatus === 'WAITLIST') return 'Apuntarme a Lista de Espera';
        return canRegisterOthers ? 'Inscribir Jugadora' : '¡Apuntarme!';
    };

    return (
        <div className="register-form-wrapper">

            {error && (
                <div className="error-banner">
                    <i className="fa fa-exclamation-triangle"></i> {error}
                </div>
            )}

            {showCapacityInfo && (
                <div className={`capacity-badge ${nextStatus === 'WAITLIST' ? 'waitlist' : 'info'}`}>
                    <i className={`fa ${nextStatus === 'WAITLIST' ? 'fa-clock-o' : 'fa-ticket'}`}></i>
                    <span>
                        {nextStatus === 'WAITLIST'
                            ? 'Plazas completas. Te inscribirás en la lista de espera.'
                            : `¡Date prisa! Quedan ${remainingSlots} plaza${remainingSlots === 1 ? '' : 's'}.`
                        }
                    </span>
                </div>
            )}

            {canRegisterOthers && (
                <div className="form-group select-wrapper">
                    <label htmlFor="player-select">Seleccionar Jugadora</label>
                    <select
                        id="player-select"
                        className="custom-select"
                        value={playerId}
                        onChange={(e) => setPlayerId(e.target.value)}
                        disabled={!isAdmin || saving}
                    >
                        <option value="" disabled>-- Busca una jugadora --</option>
                        {options.map(p => (
                            <option key={p.id} value={p.id}>
                                {p.displayName}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <button
                className={`btn-register-action ${nextStatus === 'WAITLIST' ? 'is-waitlist' : ''}`}
                onClick={submit}
                disabled={saving || (canRegisterOthers && !playerId)}
            >
                {saving ? (
                    <i className="fa fa-spinner fa-spin"></i>
                ) : (
                    <i className={`fa ${nextStatus === 'WAITLIST' ? 'fa-hourglass-half' : 'fa-check'}`}></i>
                )}
                <span>{getButtonText()}</span>
            </button>
        </div>
    );
}